import { useCallback } from "react";
import useSWR from "swr";
import { default as useSWRImmutable } from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import { AdminSystem, SearchAdminSystem, SearchSystem, System, UpdateSystemBody } from "./types/systems";

export const useSystemsApi = () => {
  const api = useApi();

  const useSystems = useCallback(
    (signal?: AbortSignal) => {
      const url = `/systems`;
      return useSWRImmutable<SearchSystem[], CustomError, string>(url, (url) =>
        api.get<SearchSystem[]>({ url, signal }),
      );
    },
    [api],
  );

  const getSystems = useCallback(
    (signal: AbortSignal) => {
      const url = `/systems`;
      return useSWRMutation<System[], CustomError, string>(url, (url) => api.get<System[]>({ url, signal }));
    },
    [api],
  );

  const getSystem = useCallback(
    (signal: AbortSignal) => {
      const url = `/systems`;
      return useSWRMutation<System, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<System>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const getAdminSystems = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/systems`, query);
      return useSWR<Paginated<SearchAdminSystem>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchAdminSystem>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminSystem = useCallback(
    (signal: AbortSignal) => {
      const url = "/admin/systems";
      return useSWRMutation<AdminSystem, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminSystem>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const updateAdminSystem = useCallback(
    async (systemId: string, body: UpdateSystemBody): Promise<void> => {
      const url = `/admin/systems/${systemId}`;
      await api.put<UpdateSystemBody>(url, body);
      mutateCache(["/systems", `/systems/${systemId}`, "/admin/systems"], url);
    },
    [api],
  );

  const deleteAdminSystem = useCallback(
    async (systemId: string): Promise<void> => {
      await api.del(`/admin/systems/${systemId}`);
      mutateCache(["/systems", `/systems/${systemId}`, "/admin/systems"]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (systemId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/systems/${systemId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminSystemVisibility = useCallback(
    async (systemId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/systems/${systemId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/systems"]);
    },
    [api],
  );

  const updateAdminSystemOrder = useCallback(
    async (systemId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/systems/${systemId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/systems"]);
    },
    [api],
  );

  return {
    useSystems,
    getSystems,
    getSystem,
    getAdminSystems,
    getAdminSystem,
    updateAdminSystem,
    updateAdminSystemVisibility,
    updateAdminSystemOrder,
    deleteAdminSystem,
    generateUploadImageUrl,
  };
};
